import type { Bundle } from './types';

/**
 * Product-agnostic async bundle-loading factory.
 *
 * Generalizes the pattern that used to live directly in `get_bundle.ts`
 * (now `src/ledger/loader.ts`): a product's async loader is just a
 * `Record<Locale, () => Promise<Bundle>>` of per-locale layer-composition
 * thunks plus a synchronous fallback. This factory does nothing more than
 * dispatch to the right thunk — it holds no knowledge of any specific
 * product's locale set, merge order, or string content, so it belongs in
 * `runtime/`, not `ledger/`.
 *
 * @typeParam L - The product's locale union (e.g. `AppLocale`).
 */
export interface AsyncCatalogDefinition<L extends string> {
  /** Returns the fallback bundle synchronously (must not await anything). */
  getFallbackBundle: () => Bundle;
  /** Per-locale loader thunks. Every member of `L` MUST have an entry. */
  loaders: Readonly<Record<L, () => Promise<Bundle>>>;
  /**
   * Human-readable catalog name for the console-error message on the
   * unreachable-at-compile-time default path (mirrors the
   * `[jumpstone-i18n] Unknown locale: …` message the hand-written switch
   * statements used to log).
   */
  catalogName: string;
}

/**
 * Builds an async `loadBundle(locale)` function from a per-locale set of
 * loader thunks. Each product's `loader.ts` (e.g. `src/ledger/loader.ts`)
 * calls this once with its own `AsyncCatalogDefinition` to get its
 * `loadBundle` export.
 *
 * `L` is a closed union, so every key SHOULD be present in `loaders` and
 * TypeScript enforces that at the call site (`Record<L, …>`). The runtime
 * still defends against a value that reaches this function despite the
 * type system (e.g. an unvalidated string cast at a boundary) exactly like
 * the hand-written `switch` statements this factory replaces did: log and
 * fall back to `getFallbackBundle()` rather than throwing or returning
 * `undefined`.
 */
export function createAsyncBundleLoader<L extends string>(
  definition: AsyncCatalogDefinition<L>,
): (locale: L) => Promise<Bundle> {
  return async function loadBundle(locale: L): Promise<Bundle> {
    // Widen to Partial for this lookup only: `Record<L, …>` correctly
    // enforces "every key present" at every call site that CONSTRUCTS a
    // definition, but that same completeness makes `loaders[locale]`
    // provably non-undefined to the type checker — defeating the runtime
    // guard below, which exists specifically for a `locale` value that
    // reaches this function despite the type system (an unvalidated string
    // cast at a boundary). Named-type cast, not `any`/`unknown`.
    const loaders = definition.loaders as Partial<
      Record<L, () => Promise<Bundle>>
    >;
    const loader = loaders[locale];
    if (!loader) {
      console.error(
        `[jumpstone-i18n:${definition.catalogName}] Unknown locale: ${locale}, falling back to fallback bundle`,
      );
      return definition.getFallbackBundle();
    }
    return loader();
  };
}
